import React from 'react';
import { Link } from 'gatsby';

import LogoSVG from '../svg/icons/graphcms.svg';
import ArrowSVG from '../svg/icons/arrow-right.svg';
import GraphQLSVG from '../svg/icons/graphql.svg';
import SlackSVG from '../svg/icons/slack.svg';
import LayersSVG from '../svg/icons/layers.svg';
import GameControllerSVG from '../svg/icons/game_controller.svg';
import LegacyLogoSVG from '../svg/icons/graphcms-legacy.svg';
import BulbSVG from '../svg/icons/bulb.svg';
import PulseSVG from '../svg/icons/pulse.svg';
import CodeSVG from '../svg/icons/code.svg';

function DocsQuickstart() {
  return (
    <div className="pt-6 space-y-6">
      <div className="border-t border-primary-150 pt-6 space-y-6">
        <Link
          to="/blog/graphcms-quickstart"
          className="group no-underline bg-white shadow rounded p-4 flex items-center space-x-4"
          target="_blank"
          rel="noopener noreferrer"
        >
          <LogoSVG className="text-cornflower w-8 h-8 lg:w-10 lg:h-10 flex-shrink-0" />

          <div className="w-full">
            <p className="font-medium text-cornflower text-lg">
              Get Started with GraphCMS
            </p>
            <p className="hidden text-sm md:block text-gray-700">
              Learn how to create a project, create a schema, and publish your
              first content.
            </p>
          </div>

          <ArrowSVG className="hidden md:block flex-shrink-0 text-gray-500 group-hover:text-cornflower w-8 h-8" />
        </Link>

        <Link
          to="/academy"
          className="group no-underline bg-white shadow rounded p-4 flex items-center space-x-4"
          target="_blank"
          rel="noopener noreferrer"
        >
          <GraphQLSVG className="text-rose w-8 h-8 lg:w-10 lg:h-10 flex-shrink-0" />

          <div className="w-full">
            <p className="font-medium text-rose text-lg">GraphCMS Academy</p>
            <p className="hidden text-sm md:block text-gray-700">
              Learn more about GraphQL, Jamstack, Headless APIs, and more.
            </p>
          </div>

          <ArrowSVG className="hidden md:block flex-shrink-0 text-gray-500 group-hover:text-rose w-8 h-8" />
        </Link>
      </div>

      <div className="border-t border-primary-150 pt-6">
        <p className="mb-2 text-primary-300 uppercase tracking-wider font-medium text-sm lg:text-xs">
          Using the API
        </p>
        <div className="pt-3 grid gap-3 md:gap-6 md:grid-cols-2">
          <Link
            to="/docs/schema/models"
            className="no-underline bg-white shadow rounded p-4 font-medium text-haiti text-lg hover:text-cornflower flex items-center"
          >
            Content Models
          </Link>
          <Link
            to="/docs/schema/system-fields"
            className="no-underline bg-white shadow rounded p-4 font-medium text-haiti text-lg hover:text-cornflower flex items-center"
          >
            System Fields
          </Link>
          <Link
            to="/docs/content-api/queries"
            className="no-underline bg-white shadow rounded p-4 font-medium text-haiti text-lg hover:text-cornflower flex items-center"
          >
            Query API
          </Link>
          <Link
            to="/docs/content-api/mutations"
            className="no-underline bg-white shadow rounded p-4 font-medium text-haiti text-lg hover:text-cornflower flex items-center"
          >
            Mutations API
          </Link>
          <Link
            to="/docs/content-api/assets"
            className="no-underline bg-white shadow rounded p-4 font-medium text-haiti text-lg hover:text-cornflower flex items-center"
          >
            Assets
          </Link>
          <Link
            to="/docs/content-api/localization"
            className="no-underline bg-white shadow rounded p-4 font-medium text-haiti text-lg hover:text-cornflower flex items-center"
          >
            Localization
          </Link>
        </div>
      </div>

      <div className="border-t border-primary-150 pt-6">
        <p className="mb-2 text-primary-300 uppercase tracking-wider font-medium text-sm lg:text-xs">
          Go further
        </p>
        <div className="py-3 grid gap-3 md:gap-6 md:grid-cols-2">
          <Link
            to="/blog/tag/projects-and-examples"
            className="group no-underline bg-white shadow rounded p-4 flex items-center space-x-4"
          >
            <LayersSVG className="text-gray-500 w-5 h-5 lg:w-8 lg:h-8 flex-shrink-0" />

            <div>
              <p className="font-medium text-haiti text-lg group-hover:text-cornflower">
                Tutorials
              </p>
            </div>
          </Link>
          <Link
            to="/docs/develop/code-examples"
            className="group no-underline bg-white shadow rounded p-4 flex items-center space-x-4"
          >
            <GameControllerSVG className="text-gray-500 w-5 h-5 lg:w-8 lg:h-8 flex-shrink-0" />

            <div>
              <p className="font-medium text-haiti text-lg group-hover:text-cornflower">
                Code Examples
              </p>
            </div>
          </Link>
          <a
            href="https://slack.graphcms.com"
            className="group no-underline bg-white shadow rounded p-4 flex items-center space-x-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            <SlackSVG className="text-gray-500 w-5 h-5 lg:w-8 lg:h-8 flex-shrink-0" />

            <div>
              <p className="font-medium text-haiti text-lg group-hover:text-cornflower">
                Join us on Slack
              </p>
            </div>
          </a>
          <a
            href="https://docs.graphcms.com"
            className="group no-underline bg-white shadow rounded p-4 flex items-center space-x-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            <LegacyLogoSVG className="text-gray-500 w-5 h-5 lg:w-8 lg:h-8 flex-shrink-0" />

            <div>
              <p className="font-medium text-haiti text-lg group-hover:text-cornflower">
                Legacy API Docs
              </p>
            </div>
          </a>
          <a
            href="https://graphcms.nolt.io"
            className="group no-underline bg-white shadow rounded p-4 flex items-center space-x-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            <BulbSVG className="text-gray-500 w-5 h-5 lg:w-8 lg:h-8 flex-shrink-0" />

            <div>
              <p className="font-medium text-haiti text-lg group-hover:text-cornflower">
                Feature Requests
              </p>
            </div>
          </a>
          <a
            href="https://status.graphcms.com"
            className="group no-underline bg-white shadow rounded p-4 flex items-center space-x-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            <PulseSVG className="text-gray-500 w-5 h-5 lg:w-8 lg:h-8 flex-shrink-0" />

            <div>
              <p className="font-medium text-haiti text-lg group-hover:text-cornflower">
                API Status
              </p>
            </div>
          </a>
          <Link
            to="/changelog"
            className="group no-underline bg-white shadow rounded p-4 flex items-center space-x-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            <CodeSVG className="text-gray-500 w-5 h-5 lg:w-8 lg:h-8 flex-shrink-0" />

            <div>
              <p className="font-medium text-haiti text-lg group-hover:text-cornflower">
                Changelog
              </p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default DocsQuickstart;
